import { PostsGridOptionsType } from "@/types/ModuleOptionsType";
import PostCard, {
  PostsGridElementType,
} from "../../components/PostCard/PostCard";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Section } from "../../components/Section/Section";
import styles from "./PostsGrid.module.scss";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { fetchPosts } from "../../getData/fetchPosts";
import { useRouter } from "next/router";
import { Modul_Post_Grid } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { ImageType } from "@/types/baseTypes";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import buttonStyles from "../../components/Button/Button.module.scss";
import { Headline } from "@/components/Headline/Headline";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import { mockPost } from "@/ui/mock-data";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";

export type PostsGridModuleType = Modul_Post_Grid;

interface PostsGridProps {
  data: PostsGridModuleType;
  postsGridOptions: PostsGridOptionsType;
  fallback_image?: ImageType | false;
  categories: CategoryOverviewPages[];
}
export type PostsGridDataType = {
  totalPosts: number;
  elements: PostsGridElementType[];
};
{
  /* jscpd:ignore-start */
}
const PostsGridModule = ({
  data,
  postsGridOptions,
  fallback_image,
  categories,
}: PostsGridProps) => {
  const {
    background_color_class,
    headline_text_align_class,
    id,
    grid_columns = "two-cols",
    section_margin_class,
    section_padding_class,
    posts_per_page,
  } = data.layout;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    show_overline_subline,
    grid_options: { grid_navigation, fallback_options },
    media_format,
  } = postsGridOptions ?? {};

  const { headline, headline_html_tag, overline, subline } = data.grid || {};
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { getLabel } = useGlobalContext();

  const { locale, defaultLocale } = useRouter();

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  return (
    <Section
      className={`is-clipped ${styles["posts-grid"]} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      disableColumns
      id={id}
    >
      <FadeInUp>
        {headline && (
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            extraClass={headline_text_align_class}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            disableAnimation
          />
        )}
        <Grid
          enableScroll={false}
          postsPerPage={posts_per_page}
          fetchItems={({ page, postsPerPage, offset, abortController }) => {
            return fetchPosts({
              page,
              postsPerPage,
              offset,
              module: data,
              locale,
              defaultLocale,
              fallback_options,
              fallback_image,
              abortController,
            });
          }}
          renderLoading
        >
          {(gridPosts, loading) => {
            return (
              <>
                <GridContent justifyContent="center" gap="2rem">
                  {gridPosts.map((element, key) => {
                    return (
                      <GridItem
                        itemWidth={{
                          mobile: "100%",
                          tablet: "100%",
                          ...(grid_columns === "three-cols" && {
                            desktop: "33.3333333%",
                          }),
                          ...(grid_columns === "two-cols" && {
                            desktop: "50%",
                          }),
                        }}
                        key={key}
                      >
                        <PostCard
                          skeleton={loading}
                          element={
                            loading
                              ? mockPost
                              : (element as PostsGridElementType)
                          }
                          readMoreLabel={getLabel("read_more_label")}
                          border_radius={show_border_radius}
                          box_shadow={show_box_shadow}
                          showBox={show_card_box}
                          grid_columns={grid_columns}
                          media_format={media_format}
                          allCategories={categories}
                        />
                      </GridItem>
                    );
                  })}
                </GridContent>
                <GridLoadMore
                  grid_navigation={grid_navigation}
                  buttonStyles={buttonStyles}
                />
              </>
            );
          }}
        </Grid>
      </FadeInUp>
    </Section>
  );
};

{
  /* jscpd:ignore-end */
}

export default PostsGridModule;
